/**
 * Persist Help Query portal credentials (submission id + signed token) so the same browser
 * can reopen `/help-query/status` without relying only on push notification links.
 */

const LS_KEY = 'indmo.help_query_portals';
const MAX_ENTRIES = 12;

export type StoredHelpQueryPortal = {
  submissionId: number;
  token: string;
  savedAt: string;
};

function parseList(raw: string | null): StoredHelpQueryPortal[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const out: StoredHelpQueryPortal[] = [];
    for (const row of parsed) {
      if (
        row &&
        typeof row === 'object' &&
        typeof (row as StoredHelpQueryPortal).submissionId === 'number' &&
        typeof (row as StoredHelpQueryPortal).token === 'string' &&
        typeof (row as StoredHelpQueryPortal).savedAt === 'string'
      ) {
        out.push(row as StoredHelpQueryPortal);
      }
    }
    return out;
  } catch {
    return [];
  }
}

/** Merge / dedupe by submission id (newest wins), cap list length. */
export function rememberHelpQueryPortal(submissionId: number, token: string): void {
  if (!Number.isFinite(submissionId) || !token.trim()) return;
  try {
    const list = parseList(localStorage.getItem(LS_KEY)).filter(
      (x) => x.submissionId !== submissionId
    );
    list.push({
      submissionId,
      token: token.trim(),
      savedAt: new Date().toISOString(),
    });
    list.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
    localStorage.setItem(LS_KEY, JSON.stringify(list.slice(0, MAX_ENTRIES)));
  } catch {
    // private mode / quota
  }
}

export function getStoredHelpQueryPortals(): StoredHelpQueryPortal[] {
  try {
    return parseList(localStorage.getItem(LS_KEY));
  } catch {
    return [];
  }
}

/** Resolve portal row for a submission id, or null. */
export function getStoredPortalForSubmission(submissionId: number): StoredHelpQueryPortal | null {
  return getStoredHelpQueryPortals().find((x) => x.submissionId === submissionId) ?? null;
}
